import React, { useContext } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
  Link as RouterLink,
} from 'react-router-dom';
import { AuthProvider, AuthContext } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
// Note: AgentProvider removed - now using Zustand stores (projectListStore, currentProjectStore)
import LoginPage from './pages/LoginPage';
import SignUpPage from './pages/SignUpPage';
import MainPage from './pages/MainPage';
import ProjectsListPage from './pages/ProjectsListPage';
import { ProfilePage } from './pages/ProfilePage';
import NewAgentProjectPage from './pages/NewAgentProjectPage';
import AgentProjectPage from './pages/AgentProjectPage';
import AdminPage from './pages/AdminPage';
import ErrorDemoPage from './pages/ErrorDemoPage';
import UserGuide from './pages/UserGuide';
import AppLayout from './layouts/AppLayout';
import ErrorBoundary from './components/common/ErrorBoundary';
import { Button, Alert, Spinner } from './components/ui';
import { PrivacyPolicyPage, TermsOfServicePage } from './pages/legal';

// Smart Home Page component that redirects based on auth status
const HomePage: React.FC = () => {
  const authContext = useContext(AuthContext);

  if (!authContext) {
    return <Alert variant="error">Auth context not available.</Alert>;
  }

  if (authContext.loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="flex items-center gap-3">
          <Spinner size="md" />
          <span className="text-secondary-600">
            Loading authentication status...
          </span>
        </div>
      </div>
    );
  }

  // If user is authenticated, redirect to main page
  if (authContext.currentUser) {
    return <Navigate to="/main" replace />;
  }

  // Studio SSO: Designer has no local landing/login — send unauthenticated
  // users to /login, which auto-redirects to Studio's hosted /signin. This
  // matches Hub's flow: visiting the app bounces straight to Studio rather
  // than showing a Sign In / Sign Up screen.
  if (import.meta.env.VITE_AUTH_PROVIDER === 'studio_sso') {
    return <Navigate to="/login" replace />;
  }

  // If not authenticated (Firebase mode), show welcome screen
  return (
    <div className="container mx-auto px-4">
      <div className="my-8 text-center">
        <h1 className="text-3xl font-bold text-secondary-900 mb-2">
          Welcome to Agent Designer
        </h1>
        <p className="text-secondary-600 mb-8">
          Please log in or sign up to continue.
        </p>
        <div className="flex flex-row gap-4 justify-center">
          <RouterLink to="/login">
            <Button variant="primary" size="lg">
              Sign In
            </Button>
          </RouterLink>
          <RouterLink to="/signup">
            <Button variant="outline" size="lg">
              Sign Up
            </Button>
          </RouterLink>
        </div>
      </div>
    </div>
  );
};

// ProtectedRoute component
interface ProtectedRouteProps {
  children?: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = () => {
  const authContext = useContext(AuthContext);
  const location = useLocation();

  if (!authContext) {
    return (
      <Alert variant="error">
        Auth context not available in ProtectedRoute.
      </Alert>
    );
  }

  if (authContext.loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="flex items-center gap-3">
          <Spinner size="md" />
          <span className="text-secondary-600">
            Loading authentication status...
          </span>
        </div>
      </div>
    );
  }

  if (!authContext.currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <Outlet />;
};

// AdminProtectedRoute component - requires ADMIN role
interface AdminProtectedRouteProps {
  children?: React.ReactNode;
}

const AdminProtectedRoute: React.FC<AdminProtectedRouteProps> = () => {
  const authContext = useContext(AuthContext);
  const location = useLocation();

  if (!authContext) {
    return (
      <Alert variant="error">
        Auth context not available in AdminProtectedRoute.
      </Alert>
    );
  }

  if (authContext.loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="flex items-center gap-3">
          <Spinner size="md" />
          <span className="text-secondary-600">
            Loading authentication status...
          </span>
        </div>
      </div>
    );
  }

  if (!authContext.currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!authContext.isAdmin) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <Alert variant="error" className="mb-4">
            <h6 className="font-semibold mb-2">Access Denied</h6>
            <p className="mb-2">
              You do not have administrator privileges to access this page.
            </p>
            <p className="text-sm opacity-80">
              Current role: {authContext.systemRole || 'USER'}
            </p>
          </Alert>
          <RouterLink to="/main">
            <Button variant="primary" className="mt-4">
              Return to Home
            </Button>
          </RouterLink>
        </div>
      </div>
    );
  }

  return <Outlet />;
};

function App() {
  return (
    <ErrorBoundary title="Application Error">
      <ThemeProvider defaultTheme="light">
        <AuthProvider>
          <Router
            future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
          >
            <Routes>
              <Route element={<AppLayout />}>
                {/* AppLayout wraps all pages */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/signup" element={<SignUpPage />} />
                <Route path="/" element={<HomePage />} />
                {/* Legal pages (public, no auth required) */}
                <Route path="/privacy" element={<PrivacyPolicyPage />} />
                <Route path="/terms" element={<TermsOfServicePage />} />
                {/* Protected Routes */}
                <Route element={<ProtectedRoute />}>
                  <Route path="/main" element={<MainPage />} />
                  <Route
                    path="/dashboard"
                    element={<Navigate to="/main" replace />}
                  />
                  <Route path="/projects" element={<ProjectsListPage />} />
                  <Route
                    path="/projects/new"
                    element={<NewAgentProjectPage />}
                  />
                  <Route path="/projects/:id" element={<AgentProjectPage />} />
                  <Route path="/profile" element={<ProfilePage />} />
                  <Route path="/error-demo" element={<ErrorDemoPage />} />
                  <Route path="/guide" element={<UserGuide />} />

                  {/* Admin routes */}
                  <Route path="/admin" element={<AdminProtectedRoute />}>
                    <Route index element={<AdminPage />} />
                    <Route path=":adminAction" element={<AdminPage />} />
                  </Route>
                </Route>
                {/* Catch-all for unmatched routes (optional) */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Route>
            </Routes>
          </Router>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
